import { Controller } from '@hotwired/stimulus';

// Truncates a comma-separated list of items to fit the available width,
// showing "+N more" when items overflow. Uses canvas text measurement
// and a ResizeObserver to recalculate on resize.
//
// Values:
//   items (Array) - JSON array of strings to display
//
// Targets:
//   text     - element whose textContent is set to the visible items
//   overflow - element shown when items are truncated ("+N more" indicator)
//   count    - element whose textContent is set to the overflow count
//   total    - element whose textContent is set to the total item count
export default class extends Controller {
  static values = { items: Array };

  static targets = ['text', 'overflow', 'count', 'total'];

  connect() {
    this.recalculate();
    if (this.itemsValue.length > 1) {
      this.observer = new ResizeObserver(() => this.recalculate());
      this.observer.observe(this.element.parentElement);
    }
  }

  disconnect() {
    this.observer?.disconnect();
  }

  itemsValueChanged() {
    this.recalculate();
  }

  recalculate() {
    const items = this.itemsValue;
    if (items.length <= 1) {
      this.textTarget.textContent = items.join('');
      this.overflowTarget.hidden = true;
      return;
    }

    const availableWidth = this.element.parentElement.clientWidth;
    const ctx = document.createElement('canvas').getContext('2d');
    ctx.font = getComputedStyle(this.element).font;
    const measure = (text) => ctx.measureText(text).width;

    if (measure(items.join(', ')) <= availableWidth) {
      this.textTarget.textContent = items.join(', ');
      this.overflowTarget.hidden = true;
      return;
    }

    let count = items.length - 1;
    while (count > 1) {
      const text = `${items.slice(0, count).join(', ')} +${items.length - count} more`;
      if (measure(text) <= availableWidth) break;
      count -= 1;
    }

    this.textTarget.textContent = items.slice(0, count).join(', ');
    this.overflowTarget.hidden = false;
    this.countTarget.textContent = items.length - count;
    if (this.hasTotalTarget) {
      this.totalTarget.textContent = items.length;
    }
  }
}
